import { lazy } from 'react';
import { probeConnectivity, waitUntilReachable } from 'helpers/connectivity';

/**
 * `React.lazy` that survives a deploy landing under an already-open tab.
 *
 * Every page in both consoles is code-split, so the browser fetches a page's
 * chunk only when you first navigate to it. Chunk filenames carry a content
 * hash and a deploy replaces the whole `/static/js` directory, so a tab that
 * loaded `main.<old>.js` before the deploy asks for `4264.<old>.chunk.js`
 * after it and gets a 404. Webpack turns that into a `ChunkLoadError`, and an
 * unhandled one unmounts the entire React tree: a white page, no message.
 *
 * It surfaces at sign-in because that is where the first code-split navigation
 * happens. The login screen is bundled into `main.js`, so it renders from the
 * stale bundle perfectly well; only the console behind it needs a chunk that no
 * longer exists on the server.
 *
 * The reload below is exactly what the user does by hand today — hit the white
 * page, press refresh — except it happens on its own, before anything broken is
 * on screen. The re-fetched document names the CURRENT `main.js`, so the chunks
 * it goes on to request are the ones actually deployed.
 */

/** Guards against a reload loop. In sessionStorage so it survives the reload. Exported for the tests. */
export const RELOAD_STAMP_KEY = 'zv:chunk-reload-at';

/**
 * A second reload cannot fix what the first one didn't, so a failure inside this
 * window is treated as "already tried" and allowed to surface as a real error.
 * Long enough to cover the reload round trip, short enough that a genuinely new
 * deploy later in the same session still self-heals.
 */
export const RELOAD_COOLDOWN_MS = 15000;

/**
 * Webpack reports a missing chunk in more than one shape — `ChunkLoadError` for
 * JS, a `CSS_CHUNK_LOAD_FAILED` code for stylesheets — and a chunk answered with
 * `index.html` by an SPA rewrite fails as a module-parse error instead, so the
 * message is matched too.
 *
 * Deliberately NOT a catch-all: an import that throws because the module's own
 * top-level code is broken has to reach the error boundary. Reloading for that
 * would fail again identically and hide the real fault.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  if (error.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  const message = String(error.message || '');
  return (
    /Loading (CSS )?chunk\s+\S+\s+failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

/** sessionStorage throws outright in some privacy modes; it must never be the thing that breaks the app. */
function readReloadStamp() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeReloadStamp(value) {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(value));
  } catch {
    /* best effort — the cooldown is a safety net, not a requirement */
  }
}

/**
 * The retry policy, without React.
 *
 * Split out of the `lazy()` callback so it can be exercised directly: the
 * policy ends in `window.location.reload()`, which jsdom does not implement,
 * so the tests hand in a `reload` of their own. Production callers pass no
 * `deps` and get the real probe, the real wait and the real reload.
 */
export async function loadChunk(
  factory,
  { probe = probeConnectivity, waitForNetwork = waitUntilReachable, reload = () => window.location.reload() } = {},
) {
  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;

    // One plain retry first. A dropped connection looks identical to a
    // missing chunk from here, and reloading the whole app to recover from a
    // network blip is a heavier answer than the problem deserves.
    try {
      return await factory();
    } catch (retryError) {
      if (!isChunkLoadError(retryError)) throw retryError;
    }

    // A chunk that cannot be fetched because there is NO NETWORK is not a stale
    // build. Reloading now would replace the whole SPA — and the no-network
    // page — with the browser's own offline screen, and index.html is served
    // no-store so there is nothing to fall back on. Ask the network first; if
    // it is down, hold the import (the Suspense fallback sits under the
    // overlay) until a probe succeeds, then ask for the chunk again — webpack
    // re-requests a failed chunk on the next call. The probe runs only after
    // the plain retry has also failed, so a blip that retry fixed pays nothing.
    if (!(await probe())) {
      await waitForNetwork();
      try {
        return await factory();
      } catch (afterError) {
        if (!isChunkLoadError(afterError)) throw afterError;
        // Still a chunk error with the network back: a deploy happened
        // meanwhile. That IS the stale-build case — fall through to it.
      }
    }

    if (Date.now() - readReloadStamp() < RELOAD_COOLDOWN_MS) {
      // Already reloaded, and the chunk is STILL missing — that is a broken
      // build, not a stale tab. Let it through to the error boundary, which
      // can say so. Reloading again would just loop.
      throw error;
    }

    writeReloadStamp(Date.now());
    reload();

    // Never settles, on purpose: the reload is already underway, and resolving
    // here would let React render into a document that is being torn down.
    return new Promise(() => {});
  }
}

export default function lazyWithRetry(factory) {
  const Component = lazy(() => loadChunk(factory));
  // Fetch the chunk before anyone navigates to it. The bundler caches the
  // module, so the lazy render that follows resolves at once instead of
  // showing the route fallback. Best effort: a failure here is retried by the
  // real load above.
  Component.preload = () => factory().catch(() => {});
  return Component;
}
